import React from 'react';

import { Box } from '@tanium/react-box';
import { Icon } from '@tanium/react-graphics';
import { CollapsedCollapsibleProps, useCollapsed } from '@tanium/react-use-collapsed';

import PageSectionContent from './PageSectionContent';
import PageSectionHeader, { Props as PageSectionHeaderProps } from './PageSectionHeader';

export interface Props extends CollapsedCollapsibleProps {
  /**
   * Optional additional content to display next to the title.
   */
  additionalTitleContent?: React.ReactNode;
  /**
   * Optional icon to display next to the title.
   */
  icon?: Icon;
  /**
   * The title to display for the section.
   */
  title: React.ReactNode;
  /**
   * The content of the section when it's collapsed.
   */
  collapsedContent?: React.ReactNode;
  /**
   * The content of the section.
   */
  children?: React.ReactNode;
}

/**
 * A generic section with a header, optionally collapsible.
 */
const PageSection = ({
  additionalTitleContent,
  children,
  defaultCollapsed,
  icon,
  isCollapsed: isCollapsedProp,
  isCollapsible,
  onToggleCollapsed,
  collapsedContent,
  title,
}: Props) => {
  const { collapseControlProps, isCollapsed } = useCollapsed(
    {
      defaultCollapsed,
      isCollapsed: isCollapsedProp,
      isCollapsible,
      onToggleCollapsed,
    },
    'PageSection',
  );

  const pageSectionHeaderProps: PageSectionHeaderProps = {
    additionalTitleContent,
    collapseControlProps,
    icon,
    isCollapsed,
    isCollapsible,
    title,
  };

  return (
    <Box as="section" display="flex" flexDirection="column">
      <PageSectionHeader {...pageSectionHeaderProps} />
      <PageSectionContent collapsedContent={collapsedContent} isCollapsed={isCollapsed}>
        {children}
      </PageSectionContent>
    </Box>
  );
};

export default PageSection;
